import React, { useEffect, useMemo, useState } from "react";
import { Mafs, Theme, Line, Polygon, Text, MovablePoint, Coordinates } from "mafs";
import { Card, Space, Typography, Segmented, Tag, Alert, Row, Col, Divider, Statistic, List } from "antd";
import {
    UserOutlined,
    CheckCircleOutlined,
    CalculatorOutlined,
    BookOutlined
} from "@ant-design/icons";

const { Title, Text: AntText } = Typography;

export const PythagorasVisualiser = ({ config, content }) => {
    const cfg = config || {};
    const [viewMode, setViewMode] = useState("CORRECT");

    const isInteractive = cfg.interactive !== false;
    const showSquares = cfg.showSquares !== false;
    const showWorked = cfg.showWorked !== false;

    const dp = Math.min(4, Math.max(0, cfg.precisionDp ?? 2));
    const format = (n) => Number(Number(n).toFixed(dp));

    const solveFor = (cfg.solveFor || "FIND_C").toString().toUpperCase();
    const units = cfg.units || "";

    // ---- Data Extraction ----
    const correctA0 = cfg.triangleLengths?.a ?? 3;
    const correctB0 = cfg.triangleLengths?.b ?? 4;
    const correctC0 = cfg.triangleLengths?.c ?? Math.hypot(correctA0, correctB0);

    const hasStudent = !!cfg.studentAnswerTriangleLengths;

    const studentA0 = cfg.studentAnswerTriangleLengths?.a ?? correctA0;
    const studentB0 = cfg.studentAnswerTriangleLengths?.b ?? correctB0;
    const studentC0 =
        cfg.studentAnswerTriangleLengths?.c ?? Math.hypot(studentA0, studentB0);

    const [aLen, setALen] = useState(viewMode === "STUDENT" ? studentA0 : correctA0);
    const [bLen, setBLen] = useState(viewMode === "STUDENT" ? studentB0 : correctB0);

    useEffect(() => {
        setALen(viewMode === "STUDENT" ? studentA0 : correctA0);
        setBLen(viewMode === "STUDENT" ? studentB0 : correctB0);
    }, [viewMode, studentA0, studentB0, correctA0, correctB0]);

    // ---- Geometry points ----
    const A = useMemo(() => [0, 0], []);
    const B = useMemo(() => [aLen, 0], [aLen]);
    const C = useMemo(() => [0, bLen], [bLen]);

    const actualHypot = Math.hypot(aLen, bLen);
    const activeC = viewMode === "STUDENT" ? studentC0 : actualHypot;

    const uBC = useMemo(() => {
        const vx = C[0] - B[0];
        const vy = C[1] - B[1];
        const len = Math.hypot(vx, vy);
        if (len < 1e-9) return [0, 0];
        return [vx / len, vy / len];
    }, [B, C]);

    const CClaimed = useMemo(() => {
        return [B[0] + uBC[0] * activeC, B[1] + uBC[1] * activeC];
    }, [B, uBC, activeC]);

    const target = aLen * aLen + bLen * bLen;
    const diff = Math.abs(target - activeC * activeC);
    const tol = Math.max(0.05, 0.02 * Math.max(1, target));
    const isPythagorean = diff <= tol;

    // ----------------------------
    // Square helpers
    // ----------------------------
    const cross2 = (vx, vy, wx, wy) => vx * wy - vy * wx;

    const squareOnSegmentOutside = (P, Q, Aref) => {
        const vx = Q[0] - P[0];
        const vy = Q[1] - P[1];
        const len = Math.hypot(vx, vy);
        if (len < 1e-9) return null;

        const perp1 = [-vy, vx];
        const perp2 = [vy, -vx];

        const ax = Aref[0] - P[0];
        const ay = Aref[1] - P[1];
        const signA = cross2(vx, vy, ax, ay);

        const chosenPerp = signA > 0 ? perp2 : perp1;

        const P2 = Q;
        const P3 = [Q[0] + chosenPerp[0], Q[1] + chosenPerp[1]];
        const P4 = [P[0] + chosenPerp[0], P[1] + chosenPerp[1]];

        return [P, P2, P3, P4];
    };

    const centroid = (pts) => {
        const s = pts.reduce(
            (acc, p) => [acc[0] + p[0], acc[1] + p[1]],
            [0, 0]
        );
        return [s[0] / pts.length, s[1] / pts.length];
    };

    const renderSquares = () => {
        if (!showSquares) return null;

        const legASquare = [A, B, [aLen, -aLen], [0, -aLen]];
        const legBSquare = [A, C, [-bLen, bLen], [-bLen, 0]];
        const hypSquarePts = squareOnSegmentOutside(B, CClaimed, A);

        const hypColor =
            viewMode === "STUDENT" && !isPythagorean ? Theme.orange : Theme.indigo;

        const hypLabel =
            viewMode === "STUDENT" ? `c² (claimed) = ${format(activeC ** 2)}` : `c² = ${format(activeC ** 2)}`;

        const hypCentroid = hypSquarePts ? centroid(hypSquarePts) : null;

        // Label Size Setting
        const labelSize = 14;

        return (
            <>
                <Polygon points={legASquare} color={Theme.green} opacity={0.12} />
                <Text x={aLen / 2} y={-aLen / 2} color={Theme.green} size={labelSize}>
                    a² = {format(aLen ** 2)}
                </Text>

                <Polygon points={legBSquare} color={Theme.blue} opacity={0.12} />
                <Text x={-bLen / 2} y={bLen / 2} color={Theme.blue} size={labelSize}>
                    b² = {format(bLen ** 2)}
                </Text>

                {hypSquarePts && (
                    <>
                        <Polygon points={hypSquarePts} color={hypColor} opacity={0.15} />
                        {hypCentroid && (
                            <Text x={hypCentroid[0]} y={hypCentroid[1]} color={hypColor} size={labelSize}>
                                {hypLabel}
                            </Text>
                        )}
                    </>
                )}
            </>
        );
    };

    const showGhost = viewMode === "STUDENT" && hasStudent && !isPythagorean;

    const pad = 8;
    const xMin = -bLen - pad;
    const xMax = aLen + pad + 2;
    const yMin = -aLen - pad;
    const yMax = bLen + pad + 2;

    return (
        <div className="ch-pythagoras-widget animate-slide-up">
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
                <Card size="small" className="ch-shapeshifter-header">
                    <Row justify="space-between" align="middle">
                        <Space>
                            <Title level={4} style={{ margin: 0 }}>
                                Pythagoras Explorer
                            </Title>
                            {hasStudent && (
                                <Segmented
                                    value={viewMode}
                                    onChange={setViewMode}
                                    options={[
                                        { label: "Correct", value: "CORRECT", icon: <CheckCircleOutlined /> },
                                        { label: "Yours", value: "STUDENT", icon: <UserOutlined /> }
                                    ]}
                                />
                            )}
                        </Space>
                        <Tag color="geekblue">{solveFor.replace("_", " ")}</Tag>
                    </Row>
                </Card>

                <div className="ch-statistics-layout" style={{ display: "flex", gap: 20 }}>
                    <div className="ch-statistics-stage" style={{ flex: 1.2 }}>
                        <Mafs height={520} viewBox={{ x: [xMin, xMax], y: [yMin, yMax] }}>
                            <Coordinates.Cartesian />
                            {renderSquares()}

                            <Line.Segment point1={A} point2={B} color={Theme.green} weight={4} />
                            <Line.Segment point1={A} point2={C} color={Theme.blue} weight={4} />

                            {showGhost && (
                                <Line.Segment point1={B} point2={C} color={Theme.gray} weight={3} opacity={0.25} />
                            )}

                            <Line.Segment
                                point1={B}
                                point2={CClaimed}
                                color={showGhost ? Theme.orange : Theme.indigo}
                                weight={5}
                            />

                            {isInteractive && solveFor !== "FIND_A" && (
                                <MovablePoint
                                    point={B}
                                    color={Theme.green}
                                    constrain={(p) => [Math.max(0.5, p[0]), 0]}
                                    onMove={(p) => setALen(p[0])}
                                />
                            )}

                            {isInteractive && solveFor !== "FIND_B" && (
                                <MovablePoint
                                    point={C}
                                    color={Theme.blue}
                                    constrain={(p) => [0, Math.max(0.5, p[1])]}
                                    onMove={(p) => setBLen(p[1])}
                                />
                            )}
                        </Mafs>
                    </div>

                    <div className="ch-statistics-panel" style={{ width: 380 }}>
                        <Space orientation="vertical" style={{ width: "100%" }}>
                            <Card size="small" title={<Space><CalculatorOutlined /> The Theorem</Space>}>
                                <div className="ch-fraction-math" style={{ textAlign: "center", fontSize: "1.9rem" }}>
                                    a² + b² = c²
                                </div>

                                <Divider style={{ margin: "12px 0" }} />

                                <div className="ch-fraction-metrics">
                                    <div className="ch-fraction-metric-card">
                                        <AntText type="secondary">Area a² + Area b²</AntText>
                                        <div className="ch-fraction-math">{format(target)}</div>
                                    </div>

                                    <div
                                        className="ch-fraction-metric-card"
                                        style={{
                                            borderLeft: `4px solid ${
                                                showGhost ? "#fa8c16" : "#52c41a"
                                            }`,
                                        }}
                                    >
                                        <AntText type="secondary">
                                            {showGhost ? "Claimed c²" : "Area c²"}
                                        </AntText>
                                        <div className="ch-fraction-math">{format(activeC ** 2)}</div>
                                    </div>
                                </div>

                                <Divider style={{ margin: "12px 0" }} />

                                <Row gutter={12}>
                                    <Col span={8}>
                                        <Statistic title="a" value={format(aLen)} suffix={units} />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic title="b" value={format(bLen)} suffix={units} />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic title="c" value={format(activeC)} suffix={units} />
                                    </Col>
                                </Row>
                            </Card>

                            {showGhost && (
                                <Alert
                                    message="Identity Mismatch"
                                    description="Your claimed c does not match this triangle’s legs (a and b)."
                                    type="error"
                                    showIcon
                                />
                            )}

                            {showWorked && (
                                <Card size="small" title={<Space><BookOutlined /> Worked Step</Space>}>
                                    <List size="small">
                                        <List.Item>
                                            1. Identify legs: a = {format(aLen)} {units}, b = {format(bLen)} {units}
                                        </List.Item>
                                        <List.Item>
                                            2. Square and add: {format(aLen ** 2)} + {format(bLen ** 2)} = {format(target)}
                                        </List.Item>
                                        <List.Item>
                                            3. So c² must be {format(target)} and c = √{format(target)} ≈ {format(Math.sqrt(target))} {units}
                                        </List.Item>
                                    </List>
                                </Card>
                            )}
                        </Space>
                    </div>
                </div>
            </Space>
        </div>
    );
};